import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { driverSupabase as supabase } from '@/integrations/supabase/driver-client';
import { activeTripStore } from '@/lib/activeTripStore';
import { GpsBuffer } from '@/plugins/gps-buffer';
import { getDriverSessionId } from '@/lib/driverSession';

const HEARTBEAT_MS_ONLINE = 60_000;
const HEARTBEAT_MS_ON_TRIP = 30_000;
// The native Android FGS POSTs heartbeats directly via HttpURLConnection.
// Target Lovable Cloud's dispatch-trip (same endpoint the JS / iOS path
// uses through `driverSupabase.functions` → `cloudSupabase`). Cloud's
// `driver_heartbeat` handler is the only one in this repo that writes
// `last_location_lat/lng/last_location_updated_at` + `presence_status`
// + `last_heartbeat_at` on the external `drivers` table using the
// service-role key. Posting straight to the external project's
// dispatch-trip (whatever version it has) was silently failing for every
// background-mode beat, which is why Admin Fleet Tracking saw nothing
// while the app was screen-off / swiped-away / backgrounded.
const CLOUD_SUPABASE_URL =
  ((import.meta as any).env?.VITE_SUPABASE_URL as string | undefined) ?? '';
const CLOUD_SUPABASE_ANON_KEY =
  ((import.meta as any).env?.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined) ?? '';

/**
 * Drives the vehicle-availability heartbeat.
 *
 * - When the driver is Online (is_available=true) OR currently on a trip,
 *   POST a heartbeat to `dispatch-trip` action `driver_heartbeat` every
 *   20s. On Android the FGS (GpsBufferService) runs the same heartbeat
 *   independently so it keeps firing while the app is backgrounded,
 *   screen is locked, or the app has been swiped away from Recents.
 * - When neither is true, stop heartbeats and emit one final `offline`
 *   heartbeat so the Admin Portal flips immediately.
 * - On-trip status overrides everything: the heartbeat payload sends
 *   `presence: 'on_trip'` so the server pins the row and the cron job
 *   never auto-expires it.
 */
export function useDriverHeartbeat(driverId: string | undefined, isOnline: boolean) {
  const jsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onTripRef = useRef<boolean>(!!activeTripStore.get());
  const activeRef = useRef<boolean>(false);
  const lastIntervalRef = useRef<number>(0);
  const isAndroidRef = useRef<boolean>(
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
  );
  const driverIdRef = useRef<string | undefined>(driverId);
  useEffect(() => { driverIdRef.current = driverId; }, [driverId]);

  // Track on-trip state from the active trip store. When on-trip flips we
  // also need to re-arm the native FGS heartbeat with the tighter 10 s
  // cadence (vs. 20 s while Online-only) so the Admin map updates faster
  // during active rides.
  useEffect(() => {
    const update = () => {
      const wasOnTrip = onTripRef.current;
      onTripRef.current = !!activeTripStore.get();
      if (wasOnTrip !== onTripRef.current && activeRef.current && driverIdRef.current) {
        // Re-issue startHeartbeat with the new cadence — idempotent on the
        // native side and instantly switches the FGS loop interval.
        const intervalMs = onTripRef.current ? HEARTBEAT_MS_ON_TRIP : HEARTBEAT_MS_ONLINE;
        lastIntervalRef.current = intervalMs;
        GpsBuffer.startHeartbeat({
          supabaseUrl: CLOUD_SUPABASE_URL,
          anonKey: CLOUD_SUPABASE_ANON_KEY,
          driverId: driverIdRef.current,
          sessionId: getDriverSessionId() ?? '',
          intervalMs,
        }).catch(() => {});
      }
    };
    update();
    return activeTripStore.subscribe(update);
  }, []);

  useEffect(() => {
    if (!driverId) return;
    const shouldRun = isOnline || onTripRef.current;

    async function fire(presence: 'online' | 'on_trip' | 'offline') {
      try {
        let lat: number | null = null;
        let lng: number | null = null;
        if (Capacitor.isNativePlatform()) {
          try {
            const { Geolocation } = await import('@capacitor/geolocation');
            const pos = await Geolocation.getCurrentPosition({
              enableHighAccuracy: false,
              timeout: 5000,
              maximumAge: 30_000,
            } as any);
            lat = pos.coords.latitude;
            lng = pos.coords.longitude;
          } catch { /* best-effort */ }
        }
        await supabase.functions.invoke('dispatch-trip', {
          body: {
            action: 'driver_heartbeat',
            driver_id: driverId,
            session_id: getDriverSessionId(),
            lat,
            lng,
            presence,
          },
        });
      } catch (err) {
        console.warn('[heartbeat] post failed', err);
      }
    }

    function startJsLoop() {
      if (jsTimerRef.current) return;
      // Immediate fire so server sees the new state without waiting one interval.
      fire(onTripRef.current ? 'on_trip' : 'online');
      jsTimerRef.current = setInterval(() => {
        fire(onTripRef.current ? 'on_trip' : 'online');
      }, onTripRef.current ? HEARTBEAT_MS_ON_TRIP : HEARTBEAT_MS_ONLINE);
    }

    function stopJsLoop() {
      if (jsTimerRef.current) {
        clearInterval(jsTimerRef.current);
        jsTimerRef.current = null;
      }
    }

    if (shouldRun) {
      activeRef.current = true;
      // On Android the native FGS is the authoritative heartbeat source —
      // it survives screen-off, swipe-from-Recents, and process kill. A
      // parallel JS interval would only fire when the WebView is alive
      // (i.e. foreground) and wakes the JS engine unnecessarily, so skip
      // it. iOS / web have no FGS, so the JS loop is the only option.
      if (!isAndroidRef.current) {
        startJsLoop();
      }
      // Native FGS heartbeat (Android only, no-op elsewhere). Survives
      // background / screen-lock / swipe-away.
      const intervalMs = onTripRef.current ? HEARTBEAT_MS_ON_TRIP : HEARTBEAT_MS_ONLINE;
      lastIntervalRef.current = intervalMs;
      GpsBuffer.startHeartbeat({
        supabaseUrl: CLOUD_SUPABASE_URL,
        anonKey: CLOUD_SUPABASE_ANON_KEY,
        driverId,
        sessionId: getDriverSessionId() ?? '',
        intervalMs,
      }).catch(() => {});
    } else {
      stopJsLoop();
      GpsBuffer.stopHeartbeat().catch(() => {});
      if (activeRef.current) {
        activeRef.current = false;
        // Final offline beat — fire-and-forget.
        fire('offline');
      }
    }

    return () => {
      stopJsLoop();
    };
  }, [driverId, isOnline]);

  // Self-heal the native FGS on app resume. If Android reclaimed the
  // service while backgrounded, this kicks it back up so heartbeats
  // resume immediately rather than waiting for the next user action.
  useEffect(() => {
    if (!isAndroidRef.current) return;
    if (!driverId) return;
    let sub: { remove: () => void } | null = null;
    (async () => {
      try {
        const h = await CapacitorApp.addListener('appStateChange', (s) => {
          if (s.isActive) GpsBuffer.ensureRunning().catch(() => {});
        });
        sub = h as any;
      } catch { /* non-native */ }
    })();
    // Also ping immediately on mount.
    GpsBuffer.ensureRunning().catch(() => {});
    return () => { try { sub?.remove(); } catch { /* ignore */ } };
  }, [driverId]);
}